import { xchacha20poly1305 } from '@noble/ciphers/chacha.js';
import { hmac } from '@noble/hashes/hmac.js';
import { sha256 } from '@noble/hashes/sha2.js';
/**
 * Конверт шифрования и слепой индекс тегов.
 *
 * Реализация лежит в `shared`, а не в `server`, потому что клиент будет
 * пользоваться ею же — сейчас для проверки, а после перехода к сквозному режиму
 * как единственный расшифровщик (раздел 7 архитектуры, этап 18 плана).
 */
export const ENVELOPE_VERSION = 1;
const ALG_XCHACHA20POLY1305 = 1;
const NONCE_LENGTH = 24;
const KEY_LENGTH = 32;
/**
 * Разделитель частей AAD — нулевой байт: он не встречается ни в одном из
 * склеиваемых значений, поэтому две разные пятёрки не могут дать один AAD.
 * Записан вызовом, а не литералом: невидимый управляющий символ в исходнике
 * переживает не каждое копирование.
 */
const AAD_SEPARATOR = String.fromCharCode(0);
const encoder = new TextEncoder();
const decoder = new TextDecoder();
export function aadBytes(aad) {
    const version = aad.version ?? ENVELOPE_VERSION;
    return encoder.encode([aad.userId, aad.entity, aad.entityId, aad.field, String(version)].join(AAD_SEPARATOR));
}
export function randomBytes(length) {
    const out = new Uint8Array(length);
    globalThis.crypto.getRandomValues(out);
    return out;
}
/** Новый ключ данных пользователя (DEK). */
export function generateDataKey() {
    return randomBytes(KEY_LENGTH);
}
/**
 * Конверт: `[версия][алгоритм][nonce 24][шифротекст с тегом]`. Самодостаточен —
 * по нему видно, чем и какой версией зашифровано, поэтому во время постепенной
 * перешифровки код умеет читать обе версии сразу (инвариант перехода из 7.5).
 */
export function seal(dek, aad, plaintext) {
    assertKey(dek);
    const nonce = randomBytes(NONCE_LENGTH);
    const cipher = xchacha20poly1305(dek, nonce, aadBytes(aad));
    const ct = cipher.encrypt(encoder.encode(plaintext));
    const envelope = new Uint8Array(2 + NONCE_LENGTH + ct.length);
    envelope[0] = ENVELOPE_VERSION;
    envelope[1] = ALG_XCHACHA20POLY1305;
    envelope.set(nonce, 2);
    envelope.set(ct, 2 + NONCE_LENGTH);
    return envelope;
}
export function open(dek, aad, envelope) {
    assertKey(dek);
    if (envelope.length <= 2 + NONCE_LENGTH) {
        throw new Error('конверт короче заголовка');
    }
    const version = envelope[0];
    const alg = envelope[1];
    if (version !== ENVELOPE_VERSION) {
        throw new Error(`неизвестная версия конверта: ${String(version)}`);
    }
    if (alg !== ALG_XCHACHA20POLY1305) {
        throw new Error(`неизвестный алгоритм конверта: ${String(alg)}`);
    }
    const nonce = envelope.subarray(2, 2 + NONCE_LENGTH);
    const ct = envelope.subarray(2 + NONCE_LENGTH);
    const cipher = xchacha20poly1305(dek, nonce, aadBytes({ ...aad, version }));
    return decoder.decode(cipher.decrypt(ct));
}
/**
 * Нормализация тега перед слепым индексом: без неё «Дом», «дом» и «дом »
 * дали бы три разных HMAC и три разных тега в фильтре.
 */
export function normalizeTag(tag) {
    return tag.normalize('NFC').trim().toLowerCase().replace(/\s+/g, ' ');
}
/**
 * Слепой индекс тега: HMAC-SHA256(ключ пользователя, нормализованный тег).
 * Фильтр по тегу превращается в сравнение HMAC и работает, не раскрывая, как тег
 * называется: сервер видит, что у восьми задач один и тот же тег, но не какой.
 */
export function blindIndex(key, tag) {
    assertKey(key);
    return hmac(sha256, key, encoder.encode(normalizeTag(tag)));
}
function assertKey(key) {
    if (key.length !== KEY_LENGTH) {
        throw new Error(`ключ должен быть длиной ${String(KEY_LENGTH)} байт`);
    }
}
//# sourceMappingURL=crypto.js.map